import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CZ_MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const CZ_DOW = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];

export const fmtCz = (iso) => { const [y, m, d] = iso.split("-"); return `${parseInt(d, 10)}. ${parseInt(m, 10)}. ${y}`; };

export function SlotCalendar({ days, onPick }) {
  const byDate = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d.slots])), [days]);
  const months = useMemo(() => [...new Set(days.map((d) => d.date.slice(0, 7)))].sort(), [days]);
  const [mi, setMi] = useState(0);
  const [selected, setSelected] = useState(null);
  const month = months[Math.min(mi, months.length - 1)];
  if (!month) return null;
  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const lead = (first.getDay() + 6) % 7;
  const dim = new Date(y, m, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: dim }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];
  const sel = selected && byDate[selected] ? selected : null;
  return (
    <div className="space-y-4" data-testid="slot-calendar">
      <div className="flex items-center justify-between">
        <button onClick={() => setMi(Math.max(0, mi - 1))} disabled={mi === 0} className="h-9 w-9 rounded-full border grid place-items-center disabled:opacity-30" data-testid="cal-prev" aria-label="Předchozí měsíc"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-semibold text-[#0A2540] capitalize" data-testid="cal-month">{CZ_MONTHS[m - 1]} {y}</div>
        <button onClick={() => setMi(Math.min(months.length - 1, mi + 1))} disabled={mi >= months.length - 1} className="h-9 w-9 rounded-full border grid place-items-center disabled:opacity-30" data-testid="cal-next" aria-label="Další měsíc"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {CZ_DOW.map((d) => <div key={d} className="text-[11px] uppercase tracking-wider text-slate-400 py-1">{d}</div>)}
        {cells.map((iso, i) => {
          if (!iso) return <div key={`e${i}`} />;
          const slots = byDate[iso];
          const free = slots?.length || 0;
          const isSel = iso === sel;
          return (
            <button key={iso} disabled={!free} onClick={() => setSelected(iso)} data-testid={`cal-day-${iso}`}
              className={`aspect-square rounded-lg border-2 text-sm flex flex-col items-center justify-center transition-colors ${
                !free ? "border-transparent text-slate-300" : isSel ? "bg-[#0A2540] border-[#0A2540] text-white" : "bg-emerald-50 border-emerald-200 text-[#0A2540] hover:border-[#0A2540]"}`}>
              <span className="font-semibold">{parseInt(iso.slice(8), 10)}</span>
              {free > 0 && <span className={`text-[10px] ${isSel ? "text-emerald-200" : "text-emerald-700"}`}>{free} {free === 1 ? "volný" : free < 5 ? "volné" : "volných"}</span>}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-500">Zeleně označené dny mají volné termíny. Klepněte na den a vyberte čas.</p>
      {sel ? (
        <div data-testid="cal-day-slots">
          <div className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">{fmtCz(sel)} – volné časy</div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {byDate[sel].map((s) => (
              <button key={s.id} onClick={() => onPick(s, sel)} data-testid={`slot-${sel}-${s.start}`}
                className="h-12 rounded-lg border-2 bg-white hover:border-[#C5A059] font-medium text-[#0A2540]">
                {s.start}–{s.end}
              </button>
            ))}
          </div>
        </div>
      ) : <p className="text-sm text-slate-500 text-center py-2" data-testid="cal-pick-hint">Vyberte den v kalendáři.</p>}
    </div>
  );
}
